import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { profileService } from '../services/profileService';
import { UPGRADE_URL } from '../config/permissions';
import type { StripeCustomerLink } from '../types/profile';

interface UseUpgradeActionResult {
  /** Send the user to the best available upgrade destination. */
  onUpgrade: () => Promise<void>;
  /** True while the billing-portal session is being created. */
  isRedirecting: boolean;
}

/**
 * The single upgrade path for every tier gate.
 *
 * **Billing portal, not Checkout.** Everyone who sees a gate is already a
 * subscriber on a lower tier, so this is a plan *change* on an existing
 * subscription. A Checkout Session would open a second subscription and bill
 * them twice; the portal applies the switch with proration. (Requires the
 * plan-switch flow to be enabled on the Stripe account's customer portal.)
 *
 * **Always does something.** Callers previously bailed out with
 * `if (!primaryCustomer) return;` and disabled the button when no Stripe
 * customer was linked, which left users whose subscription is not linked to
 * their account — guest checkout being the known case — with a dead control
 * and no way to pay us. Here a missing link falls back to the public pricing
 * page instead of failing silently.
 *
 * @param enabled Fetch the customer links only when a gate is actually shown.
 * @param fallbackUrl Overrides `UPGRADE_URL` for the no-customer path. Pass the
 *   `upgrade_url` from a server refusal (e.g. QuantForge's `ticker_not_in_plan`
 *   403) so the destination stays whatever the backend nominated.
 */
export function useUpgradeAction(
  enabled: boolean,
  fallbackUrl?: string
): UseUpgradeActionResult {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { data: stripeCustomers = [] } = useQuery<StripeCustomerLink[]>({
    queryKey: ['stripeCustomers'],
    queryFn: () => profileService.getStripeCustomerLinks(),
    enabled,
  });

  const onUpgrade = useCallback(async (): Promise<void> => {
    const primaryCustomer =
      stripeCustomers.find((c) => c.is_primary) || stripeCustomers[0];

    if (!primaryCustomer) {
      window.open(fallbackUrl ?? UPGRADE_URL, '_blank', 'noopener,noreferrer');
      return;
    }

    setIsRedirecting(true);
    try {
      const { url } = await profileService.createBillingPortalSession(
        primaryCustomer.id,
        window.location.href
      );
      window.location.href = url;
    } catch (err) {
      // Never strand the user on a failed portal call — the marketing page
      // still lets them convert.
      console.error('Failed to create billing portal session:', err);
      setIsRedirecting(false);
      window.open(fallbackUrl ?? UPGRADE_URL, '_blank', 'noopener,noreferrer');
    }
  }, [stripeCustomers, fallbackUrl]);

  return { onUpgrade, isRedirecting };
}
